import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  BaseForm,
  buildCustomFieldSelection,
  CustomFieldsSection,
  FormChipList,
  FormTag,
  SectionCard,
} from "components/BaseForm";
import { formatError } from "helpers/formatError";
import { useContactMutations } from "hooks/useContact";
import {
  SUCCESS_MESSAGES,
  TAG_MODEL_TYPE,
} from "routes/contact/form/constants";
import { buildDefaultValues, FORM_MODES } from "routes/contact/form/defaults";
import { toContactPayload } from "routes/contact/form/payload";
import { buildContactSchema } from "routes/contact/form/schema";
import DetailsSection from "routes/contact/form/sections/DetailsSection";
import EmailAddressesSection from "routes/contact/form/sections/EmailAddressesSection";
import EntityField from "routes/contact/form/sections/EntityField";
import PhoneNumbersSection from "routes/contact/form/sections/PhoneNumbersSection";

const toDisplay = (id, label) => (id != null ? { id, label } : undefined);

/**
 * The Contact create and edit form. Its caller loads every input to the
 * defaults before mounting it, because BaseForm reads them once.
 *
 * The entity is fixed on edit and when the create preset names one, so the
 * picker is disabled then and editable only on a create without an entity.
 */
const ContactForm = ({
  mode,
  record,
  preset = {},
  presetEntityName,
  customFieldConfigs,
  customFieldGroups = [],
  onSaved,
  onCancel,
}) => {
  const { create, update } = useContactMutations();
  const savedRef = useRef(null);
  const isEdit = mode === FORM_MODES.edit;
  const isEntityFixed = isEdit || preset.entityId != null;
  const entityDisplay = isEdit
    ? toDisplay(record.entity_id, record.entity_name)
    : toDisplay(preset.entityId, presetEntityName);

  const [customFieldSelection, setCustomFieldSelection] = useState(() =>
    buildCustomFieldSelection(
      customFieldConfigs,
      record ? (record.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(
    () => buildContactSchema({ customFieldConfigs }),
    [customFieldConfigs]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues({ mode, record, preset, customFieldConfigs }),
    [mode, record, preset, customFieldConfigs]
  );

  const handleSubmit = async values => {
    const data = toContactPayload(values, { customFieldConfigs, preset });
    try {
      savedRef.current = isEdit
        ? await update.mutateAsync({
            entityId: record.entity_id,
            id: record.id,
            data,
          })
        : await create.mutateAsync({ entityId: values.entity_id, data });
    } catch (error) {
      // BaseForm shows the banner and field errors from the rethrown error.
      toast.error(formatError(error, "Could not save the Contact"));
      throw error;
    }
  };

  // The update response is not the full record, so an edit reports the record
  // it was opened with.
  const handleSuccess = () => {
    toast.success(SUCCESS_MESSAGES[mode]);
    onSaved(isEdit ? record : savedRef.current);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
    >
      <div className="tw:flex tw:flex-col tw:gap-6">
        <DetailsSection
          departmentDisplay={toDisplay(
            record?.department_id,
            record?.department_name
          )}
        >
          <EntityField
            campaignId={preset.campaignId}
            display={entityDisplay}
            disabled={isEntityFixed}
          />
        </DetailsSection>
        <EmailAddressesSection />
        <PhoneNumbersSection />
        <SectionCard title="Alternate Names">
          <div className="tw:max-w-2xl">
            <FormChipList
              name="alternate_names"
              placeholder="Type a name and press Enter"
            />
          </div>
        </SectionCard>
        <SectionCard title="Tags">
          <div className="tw:max-w-2xl">
            <FormTag name="tags" label={null} modelType={TAG_MODEL_TYPE} />
          </div>
        </SectionCard>
        {customFieldSelection.length > 0 && (
          <CustomFieldsSection
            selection={customFieldSelection}
            setSelection={setCustomFieldSelection}
            groups={customFieldGroups}
          />
        )}
      </div>
    </BaseForm>
  );
};

ContactForm.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  record: PropTypes.object,
  preset: PropTypes.shape({
    entityId: PropTypes.number,
    campaignId: PropTypes.number,
    leadId: PropTypes.number,
    leadSource: PropTypes.string,
    leadSourceRecordId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  presetEntityName: PropTypes.string,
  customFieldConfigs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default ContactForm;
